/** @flow */
import type { State } from "@store/reducers";
import type {
  EmployeeAbsence,
  AbsencesState,
  EmployeeAbsenceAlert,
  AddAbsenceModal,
  TransferAbsenceModalType,
  EmployeeAbsenceShiftPatternOptions,
  EmployeeAbsenceInteraction,
  EditInteractionModal,
  EmployeeAbsenceAdditionalInfo,
  EditAdditionalInfoModal,
  AbsenceStatistics,
  AbsenceDocument,
  DeleteAbsenceDocumentModal,
  RtwInterviewForm,
  EmployeeAbsenceManagerNotes,
  EmployeeAbsenceNotification,
  EmployeeAbsenceNotificationModal,
  EmployeeNonCancelledAbsencesContent,
  TransferAbsenceConfirmationModalType,
} from "@modules/shared/reducer/employee-absences/types";
import { getPagination } from "@selectors";
import type { PaginationExtendedType, PropertiesSchema, ModalType } from "@types";

export const getEmployeeAbsences = (state: State): AbsencesState => state.shared.employeeAbsences;

export const getAbsences = (state: State): Array<EmployeeAbsence> =>
  getEmployeeAbsences(state).list.content;

export const getAbsence = (state: State): ?EmployeeAbsence =>
  getEmployeeAbsences(state).absenceDetails;

export const getAlertsTriggered = (state: State): Array<EmployeeAbsenceAlert> =>
  getEmployeeAbsences(state).alerts.content;

export const getAlertsTriggeredPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).alerts);

export const getAlertsTriggeredPage = (state: State): number | null =>
  getAlertsTriggeredPagination(state).pageNumber;

export const getAlertsTriggeredModalOpen = (state: State): boolean =>
  getEmployeeAbsences(state).modals.alertsTriggered.visible;

export const getAddAbsenceModal = (state: State): AddAbsenceModal =>
  getEmployeeAbsences(state).modals.addAbsence;

export const getTransferAbsenceModal = (state: State): TransferAbsenceModalType =>
  getEmployeeAbsences(state).modals.transferAbsence;

export const getTransferAbsenceConfirmationModal = (
  state: State,
): TransferAbsenceConfirmationModalType =>
  getEmployeeAbsences(state).modals.transferAbsenceConfirmation;

export const getAbsencesPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).list);

export const getAbsencesSchema = (state: State): ?PropertiesSchema =>
  getEmployeeAbsences(state).absencesSchema;

export const getAbsenceShiftPattern = (state: State): Array<any> =>
  getEmployeeAbsences(state).absenceShiftPattern;

export const getAbsenceShiftPatternOptions = (state: State): ?EmployeeAbsenceShiftPatternOptions =>
  getEmployeeAbsences(state).absenceShiftPatternOptions;

export const getAbsenceShiftPatternModalOpen = (state: State): boolean =>
  getEmployeeAbsences(state).modals.shiftPattern.visible;

export const getInteractions = (state: State): Array<EmployeeAbsenceInteraction> =>
  getEmployeeAbsences(state).interactions.content;

export const getInteractionsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).interactions);

export const getInteractionsPage = (state: State): number | null =>
  getInteractionsPagination(state).pageNumber;

export const getEditInteractionModal = (state: State): EditInteractionModal =>
  getEmployeeAbsences(state).modals.interaction;

export const getAdditionalInfo = (state: State): Array<EmployeeAbsenceAdditionalInfo> =>
  getEmployeeAbsences(state).additionalInfo.content;

export const getAdditionalInfoPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).additionalInfo);

export const getAdditionalInfoPage = (state: State): number | null =>
  getAdditionalInfoPagination(state).pageNumber;

export const getEditAdditionalInfoModal = (state: State): EditAdditionalInfoModal =>
  getEmployeeAbsences(state).modals.additionalInfo;

export const getAbsenceStatistics = (state: State): ?AbsenceStatistics =>
  getEmployeeAbsences(state).absenceStatistics;

export const getAbsenceStatisticsFilters = (state: State): Object =>
  ((state.form || {})["absence-statistics-filters"] || {}).values;

export const getAbsenceDocuments = (state: State): Array<AbsenceDocument> =>
  getEmployeeAbsences(state).absenceDocuments.content;

export const getRevisionHistory = (state: State) => getEmployeeAbsences(state).revisionHistory;

export const getEmployeeSMSMessages = (state: State) =>
  getEmployeeAbsences(state).employeeSMSMessages;

export const employeeNonCancelledAbsences = (state: State): EmployeeNonCancelledAbsencesContent => {
  return state.shared.employeeAbsences.employeeNonCancelledAbsences;
};

export const getAbsenceDocumentsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).absenceDocuments);

export const getAbsenceDocumentsPage = (state: State): number | null =>
  getAbsenceDocumentsPagination(state).pageNumber;

export const getAddAbsenceDocumentModal = (state: State): ModalType =>
  getEmployeeAbsences(state).modals.addAbsenceDocument;

export const getDeleteAbsenceDocumentModal = (state: State): DeleteAbsenceDocumentModal =>
  getEmployeeAbsences(state).modals.deleteAbsenceDocument;

export const getAbsencesFilters = (state: State): Object => {
  let values = ((state.form || {})["absences-filters"] || {}).values;
  return { ...values, isCancelled: (values || {}).isCancelled || false };
};

export const getRtwInterviewForm = (state: State): ?RtwInterviewForm => {
  const absenceDetails = getEmployeeAbsences(state).absenceDetails;
  return absenceDetails ? absenceDetails.rtwInterviewForms[0] : null;
};

export const getManagerNotes = (state: State): Array<EmployeeAbsenceManagerNotes> =>
  getEmployeeAbsences(state).managerNotes.content;

export const getManagerNotesPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).managerNotes);

export const deleteNoteModalVisible = (state: State) =>
  getEmployeeAbsences(state).modals.deleteManagerNote.visible;

export const deleteNoteModalSelectedNote = (state: State) =>
  getEmployeeAbsences(state).modals.deleteManagerNote.note;

export const getEmployeeAbsenceNotifications = (state: State): Array<EmployeeAbsenceNotification> =>
  getEmployeeAbsences(state).absenceNotifications.content;

export const getEmployeeAbsenceNotificationsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).absenceNotifications);

export const getEmployeeAbsenceNotificationsPage = (state: State): number | null =>
  getEmployeeAbsenceNotificationsPagination(state).pageNumber;

export const getEmployeeAbsenceNotificationModal = (
  state: State,
): EmployeeAbsenceNotificationModal => getEmployeeAbsences(state).modals.absenceNotification;

export const getAddEditManagerNotesModal = (state: State) =>
  getEmployeeAbsences(state).modals.managerNotes;

export const getManagerNotesSchema = (state: State): ?PropertiesSchema =>
  getEmployeeAbsences(state).managerNotesSchema;

export const openRtwInterviewModal = (state: State) =>
  getEmployeeAbsences(state).modals.rtwInterview.visible;

export const selectCapturedEmployeeBankHolidays = (state: State): Array<string> => {
  const data = getEmployeeAbsences(state);

  // if capture absence during bank holidays return empty array
  if (data && data.captureAbsenceBankHolidays) {
    return [];
  }

  return data.bankHolidays.map(i => i.date);
};

export const selectBankHolidays = (state: State): Array<string> => {
  return getEmployeeAbsences(state).bankHolidays.map(i => i.date);
};
